import { useNavigate } from "react-router";
import { useRef, useState } from "react";
import { addWeeks } from "date-fns";
import formatISO from 'date-fns/formatISO';
import { useAppDispatch } from "../../hooks/reduxToolkit";
import { IncidentModel, updateIncident } from "../../reduxToolkit/incidentsSlice";
import { RequireIncidentProvided, requireIncident } from "../../wrappers/requireIncident";
import { useTerminology } from "../../hooks/useTerminology";
import { useConfirm } from "../../providers/confirmDialog";
import { useSubscriptionAccess } from "../../hooks/useSubscriptionAccess";
import { formatDateInTimeZone, parseDateInTimeZone } from "../../../lib/formatDateInTimeZone";

import './incidentCreate.scss'

function IncidentEdit({ incident }: RequireIncidentProvided) {
  const { isReadOnly } = useSubscriptionAccess()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const terms = useTerminology()
  const [confirm] = useConfirm()

  const isDemoIncident = incident.description === 'Broken Leg (demo incident)'
  const isDisabled = isDemoIncident || isReadOnly

  const [row, setRow] = useState({
    description: incident.description,
    is_maternity: !!incident.is_maternity,
    maternity_due_date: incident.maternity_due_date ?? '',
    is_pre_existing_condition: !!incident.is_pre_existing_condition,
    // Add-on incidents allow a manual start date; format for the date input.
    start_date: incident.start_date ? formatDateInTimeZone(incident.start_date, { format: 'yyyy-MM-dd' }) : '',
  })

  const formRef = useRef<HTMLFormElement>(null)
  const [wasValidated, setWasValidated] = useState(false)

  const goBack = () => navigate(`/incidents/${incident.id}`)

  const doSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isDisabled) { return }
    if (!formRef?.current?.reportValidity()) {
      setWasValidated(true)
      return
    }

    const changes: Partial<IncidentModel> = {
      description: row.description,
      is_maternity: row.is_maternity,
      is_pre_existing_condition: row.is_pre_existing_condition,
    }

    if (row.is_maternity) {
      // For maternity incidents the start date is always 40 weeks before the due date.
      changes.maternity_due_date = row.maternity_due_date
      changes.start_date = addWeeks(parseDateInTimeZone(row.maternity_due_date), -40).toISOString()
    } else if (incident.is_maternity) {
      // Maternity is being turned off: confirm, clear the due date, and reset the start date.
      const confirmed = await confirm({
        title: `Is this really not a maternity ${terms.incident}?`,
        message: 'If you uncheck this box, the due date will be cleared and the start date will be set to the earliest expense date.',
      })
      if (!confirmed) { return }

      changes.maternity_due_date = null
      // The start-date middleware only reacts to expense changes, not incident updates,
      // so recompute the earliest expense date here.
      const earliest = incident.expenses.reduce((min, ex) => {
        if (ex?.date && (!min || ex.date < min)) { return ex.date }
        return min
      }, null as string | null)
      if (earliest) { changes.start_date = earliest }
    } else if (incident.is_addon_incident && row.start_date) {
      // Add-on incidents allow the user to set the start date manually.
      changes.start_date = row.start_date
    }

    dispatch(updateIncident({
      id: incident.id,
      ...changes,
      updated_at: formatISO(new Date()),
    }))
    goBack()
  }

  return <div className="incident-create">
    <div className="row incident-create__title">
      <div className="col">
        <h1>Edit {terms.Incident}</h1>
      </div>
    </div>

    <div className="row incident-create__form">
      <div className="col">
        <form ref={formRef} onSubmit={doSubmit} className={`${wasValidated && 'was-validated'}`}>
          <div className={`input-group has-validation`}>
            <label className='input-group-text'>
              <span className="d-none d-md-flex">{terms.Incident} Name</span>
              <span className="d-flex d-md-none">Name</span>
            </label>
            <input type="text" className='form-control'
              required={true}
              disabled={isDisabled}
              value={row.description}
              onChange={(e) => setRow({ ...row, description: e.target.value })} />
          </div>

          <div className={`input-group has-validation mt-2`}>
            <div className="form-check">
              <input className="form-check-input" type="checkbox"
                disabled={isDisabled}
                checked={row.is_maternity}
                onChange={() => setRow({ ...row, is_maternity: !row.is_maternity })} />
              <label className="form-check-label">
                This is a Maternity {terms.incident}
              </label>
            </div>
          </div>

          {row.is_maternity && <div className={`input-group has-validation mt-2`}>
            <label className='input-group-text'>Due Date</label>
            <input type="date" className='form-control'
              required={row.is_maternity}
              disabled={isDisabled}
              value={row.maternity_due_date}
              onChange={(e) => setRow({ ...row, maternity_due_date: e.target.value })} />
          </div>}

          {!row.is_maternity && incident.is_addon_incident && <div className={`input-group has-validation mt-2`}>
            <label className='input-group-text'>Start Date</label>
            <input type="date" className='form-control'
              disabled={isDisabled}
              value={row.start_date}
              onChange={(e) => setRow({ ...row, start_date: e.target.value })} />
          </div>}

          <div className={`input-group has-validation mt-2`}>
            <div className="form-check">
              <input className="form-check-input" type="checkbox"
                disabled={isDisabled}
                checked={row.is_pre_existing_condition}
                onChange={() => setRow({ ...row, is_pre_existing_condition: !row.is_pre_existing_condition })} />
              <label className="form-check-label">
                This is a Pre-existing Condition
              </label>
            </div>
          </div>

          <div className="col-12 d-flex mt-3">
            <button type='submit' className={`btn btn-primary me-2`} disabled={isDisabled}>
              Save
            </button>
            <button type='button' className={`btn btn-outline-secondary`} onClick={goBack}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
}

export default requireIncident(IncidentEdit)
